/**
 * Thin fetch wrapper around the RepoHunt FastAPI backend.
 *
 * Cookie auth (session) — `credentials: "include"` ships the session cookie
 * (same-origin in dev via the Vite proxy, single-origin in prod). On 401 the
 * SPA bounces to the server-rendered /login page.
 */

export interface ApiErrorBody {
  detail?: unknown;
  retry_after?: number | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(message: string, status: number, body: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function detailMessage(body: ApiErrorBody, fallback: string): string {
  if (typeof body.detail === "string") return body.detail;
  return fallback;
}

// On 401 the SPA bounces to /login. Indirected through a handler so tests can
// override it without fighting jsdom's non-navigable window.location.
let onUnauthorized: () => void = () => window.location.assign("/login");

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export interface ApiFetchOptions extends Omit<RequestInit, "headers" | "body"> {
  headers?: Record<string, string>;
  json?: unknown;
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const { json, headers, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    credentials: "include",
    headers: { Accept: "application/json", ...headers },
  };
  if (json !== undefined) {
    init.method = init.method ?? "POST";
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  }

  const res = await fetch(path, init);

  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError("Требуется вход", 401, {});
  }

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { detail: text };
    }
  }

  if (!res.ok) {
    const errBody = (body ?? {}) as ApiErrorBody;
    throw new ApiError(detailMessage(errBody, `Ошибка ${res.status}`), res.status, errBody);
  }

  return body as T;
}
